import React, { useEffect, useState, useRef } from 'react';

interface CryptoPrice {
  usd: number;
  usd_24h_change: number;
  image?: string;
  symbol?: string;
}

interface CryptoPrices {
  [key: string]: CryptoPrice;
}

const CryptoTicker: React.FC = () => {
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrices | null>(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=50&page=1&sparkline=false&price_change_percentage=24h'
        );
        const data = await response.json();

        const formattedData: CryptoPrices = {};
        data.forEach((coin: any) => {
          formattedData[coin.id] = {
            usd: coin.current_price,
            usd_24h_change: coin.price_change_percentage_24h || 0,
            image: coin.image,
            symbol: coin.symbol,
          };
        });

        setCryptoPrices(formattedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        setLoading(false);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!scrollRef.current || loading || !cryptoPrices) return;

    const scrollContainer = scrollRef.current;

    const animate = () => {
      setScrollPosition(prev => {
        const newPosition = prev + 1;
        if (newPosition >= scrollContainer.scrollWidth / 2) {
          return 0;
        }
        return newPosition;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    const animationRef = { current: requestAnimationFrame(animate) };

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [loading, cryptoPrices]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 py-2 text-center text-sm text-slate-600 dark:text-slate-400">
        Loading cryptocurrency prices...
      </div>
    );
  }

  const renderCryptoItem = (coinId: string, priceData: CryptoPrice, key: string) => {
    const displayName = coinId.charAt(0).toUpperCase() + coinId.slice(1);
    const symbol = priceData.symbol ? priceData.symbol.toUpperCase() : '';
    const priceChangeColor =
      priceData.usd_24h_change >= 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-red-600 dark:text-red-400';

    return (
      <div key={key} className="inline-flex items-center mx-4">
        {priceData.image && (
          <img src={priceData.image} alt={`${displayName} logo`} className="w-5 h-5 mr-2" />
        )}
        <span className="font-medium text-slate-900 dark:text-slate-100">{symbol}</span>
        <span className="mx-2 text-slate-700 dark:text-slate-300">
          $
          {priceData.usd.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
        <span className={`${priceChangeColor}`}>
          {priceData.usd_24h_change >= 0 ? '+' : ''}
          {priceData.usd_24h_change.toFixed(2)}%
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-800 py-3 overflow-hidden border-b border-slate-200 dark:border-slate-700">
      <div
        ref={scrollRef}
        className="whitespace-nowrap"
        style={{
          transform: `translateX(-${scrollPosition}px)`,
          width: 'fit-content',
        }}
      >
        {cryptoPrices &&
          Object.entries(cryptoPrices).map(([coinId, priceData], index) => (
            <>
              {renderCryptoItem(coinId, priceData, `original-${index}`)}
              {renderCryptoItem(coinId, priceData, `duplicate-${index}`)}
            </>
          ))}
      </div>
    </div>
  );
};

export default CryptoTicker;
